import type { PublicSessionData } from '@/lib/api/types';
import type { AnalyticsUserProfile } from '@/types/analytics-user-profile';
import type { BrowsingPreference } from '@/lib/analytics/browsing-preference';
import type { DataLayerUserInfo } from './data-layer';

type CookiebotConsentWindow = Window & {
    Cookiebot?: {
        consent?: { marketing?: boolean; statistics?: boolean };
        hasResponse?: boolean;
    };
};

function getCookiebot() {
    return (window as CookiebotConsentWindow).Cookiebot;
}

function hasCookiebotScript(): boolean {
    return Boolean(document.getElementById('Cookiebot') || document.querySelector('script[src*="cookiebot.com"]'));
}

/** True when Cookiebot is on the page (object or script). */
export function isCookiebotPresent(): boolean {
    return Boolean(getCookiebot()) || hasCookiebotScript();
}

/**
 * First `log_state` may fire only after consent is known.
 * No Cookiebot (local) → ready immediately. Cookiebot present → wait for `hasResponse`.
 */
export function isCookiebotConsentReady(): boolean {
    if (!isCookiebotPresent()) {
        return true;
    }
    return getCookiebot()?.hasResponse === true;
}

/** G1 + advertising (marketing) + analytics (statistics), e.g. G101 = ads denied, analytics granted. */
export function resolveLogStateConsentState(): DataLayerUserInfo['consent_state'] {
    const consent = getCookiebot()?.consent;
    const advertising = consent?.marketing === true;
    const analytics = consent?.statistics === true;
    if (advertising && analytics) return 'G111';
    else if (advertising && !analytics) return 'G110';
    else if (!advertising && analytics) return 'G101';
    else return 'G100';
}

export function resolveAnalyticsUserId(
    auth: PublicSessionData,
    analyticsProfile: AnalyticsUserProfile | null | undefined
): string {
    if (auth.userType === 'guest') {
        return '';
    }
    return analyticsProfile?.crm_profile_id?.trim() || auth.customerId || auth.encUserId || '';
}

/** CRM/order fields apply only to registered sessions (avoids stale profile after logout). */
export function resolveAnalyticsProfileForLogState(
    auth: PublicSessionData,
    analyticsProfile: AnalyticsUserProfile | null | undefined
): AnalyticsUserProfile | null {
    return auth.userType === 'registered' ? (analyticsProfile ?? null) : null;
}

export function buildLogStateUserInfo(
    auth: PublicSessionData,
    analyticsProfile: AnalyticsUserProfile | null | undefined,
    browsingPreference?: BrowsingPreference
): DataLayerUserInfo {
    const profile = resolveAnalyticsProfileForLogState(auth, analyticsProfile);

    return {
        id: resolveAnalyticsUserId(auth, profile),
        type: auth.userType === 'registered' ? 'member' : 'guest',
        first_name: auth.firstName ?? '',
        last_name: auth.lastName ?? '',
        consent_state: resolveLogStateConsentState(),
        ...(profile?.email_address ? { email_address: profile.email_address } : {}),
        ...(profile?.phone_number ? { phone_number: profile.phone_number } : {}),
        ...(profile?.marketing_consent !== undefined ? { marketing_consent: profile.marketing_consent } : {}),
        ...(profile?.street ? { street: profile.street } : {}),
        ...(profile?.city ? { city: profile.city } : {}),
        ...(profile?.zip_code ? { zip_code: profile.zip_code } : {}),
        ...(profile?.region ? { region: profile.region } : {}),
        ...(profile?.country ? { country: profile.country } : {}),
        membership_status: profile?.membership_status ? profile.membership_status : 'unknown',
        ...(profile?.orders_count !== undefined ? { orders_count: profile.orders_count } : {}),
        ...(profile?.last_order_date ? { last_order_date: profile.last_order_date } : {}),
        ...(profile?.first_order_date ? { first_order_date: profile.first_order_date } : {}),
        ...(browsingPreference ? { browsing_preference: browsingPreference } : {}),
    };
}
